import { useCallback } from 'react';
import { useDimmingContext } from '@/context/DimmingContext';
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';

type CalendarVariant = 'full' | 'insights' | 'timetracking';
type EmployeeFilter = string | null;
type StatusFilter = string | null;

/**
 * Custom hook for shift dimming functionality
 * Provides simplified access to dimming context with performance optimizations
 *
 * Note: This hook subscribes to the full dimming context.
 * For individual shift components that only need dimming calculations,
 * consider using useShiftDimming() instead for better performance.
 */
export const useDimming = () => {
  const context = useDimmingContext();

  // Extract values from context
  // Note: With context value now memoized, this only causes rerenders when actual state changes
  const {
    selectedEmployeeFilter,
    selectedStatusFilter,
    hoveredShiftId,
    shiftWithOpenSelect,
    activeTooltipShiftId,
    draggingShiftId,
    setSelectedEmployeeFilter,
    setSelectedStatusFilter,
    setHoveredShiftId,
    setShiftWithOpenSelect,
    setActiveTooltipShiftId,
    setDraggingShiftId,
    getDimmingClass,
    shouldDimForEmployeeFilter,
    shouldDimForStatusFilter,
    shouldShowTooltip,
    getTooltipSide,
    clearAllDimming,
  } = context;

  // Memoized dimming class getter for individual shifts
  const getShiftDimmingClass = useCallback((
    shiftId: string,
    employeeId?: string | null,
    calendarVariant?: CalendarVariant,
    shiftStatus?: string
  ) => {
    return getDimmingClass(shiftId, employeeId, calendarVariant, shiftStatus);
  }, [getDimmingClass]);

  // Convenience method for checking if a shift should be highlighted (not dimmed)
  const isShiftHighlighted = useCallback((
    shiftId: string,
    employeeId?: string | null,
    calendarVariant: CalendarVariant = 'full',
    shiftStatus?: string
  ): boolean => {
    // Select open takes highest priority
    if (shiftWithOpenSelect) {
      return shiftWithOpenSelect === shiftId;
    }

    // Hover highlighting (not for insights)
    if (hoveredShiftId && calendarVariant !== 'insights') {
      return hoveredShiftId === shiftId;
    }

    // Combined filter highlighting (AND logic)
    if (selectedEmployeeFilter || selectedStatusFilter) {
      const shouldDimForEmployee = shouldDimForEmployeeFilter(selectedEmployeeFilter, employeeId);
      const shouldDimForStatus = shouldDimForStatusFilter(selectedStatusFilter, shiftStatus);
      
      // Highlight if neither filter says to dim (both must pass to avoid dimming)
      return !(shouldDimForEmployee || shouldDimForStatus);
    }

    // No highlighting active
    return true;
  }, [shiftWithOpenSelect, hoveredShiftId, selectedEmployeeFilter, selectedStatusFilter, shouldDimForEmployeeFilter, shouldDimForStatusFilter]);

  // Convenience method for setting employee filter with common values
  const setEmployeeFilter = useCallback((filter: EmployeeFilter) => {
    setSelectedEmployeeFilter(filter);
  }, [setSelectedEmployeeFilter]);

  // Shorthand methods for common filter values
  const showAllEmployees = useCallback(() => {
    setEmployeeFilter('all');
  }, [setEmployeeFilter]);

  const showUnassignedShifts = useCallback(() => {
    setEmployeeFilter('unassigned');
  }, [setEmployeeFilter]);

  const showEmployeeShifts = useCallback((employeeId: string) => {
    setEmployeeFilter(employeeId);
  }, [setEmployeeFilter]);

  const clearEmployeeFilter = useCallback(() => {
    setEmployeeFilter(null);
  }, [setEmployeeFilter]);

  // Status filter management functions  
  const setStatusFilter = useCallback((filter: string | null) => {
    setSelectedStatusFilter(filter);
  }, [setSelectedStatusFilter]);

  const showAllStatuses = useCallback(() => {
    setStatusFilter('all');
  }, [setStatusFilter]);

  const showStatusShifts = useCallback((status: string) => {
    setStatusFilter(status);
  }, [setStatusFilter]);

  const clearStatusFilter = useCallback(() => {
    setStatusFilter(null);
  }, [setStatusFilter]);

  // Enhanced hover management with variant support
  const handleShiftHover = useCallback((
    shiftId: string | null, 
    calendarVariant?: CalendarVariant
  ) => {
    setHoveredShiftId(shiftId, calendarVariant);
  }, [setHoveredShiftId]);

  // Select state management
  const handleSelectOpen = useCallback((shiftId: string) => {
    setShiftWithOpenSelect(shiftId);
  }, [setShiftWithOpenSelect]);

  const handleSelectClose = useCallback(() => {
    setShiftWithOpenSelect(null);
  }, [setShiftWithOpenSelect]);

  // Utility to get current dimming state summary
  const getDimmingState = useCallback(() => {
    return {
      hasEmployeeFilter: !!selectedEmployeeFilter,
      hasStatusFilter: !!selectedStatusFilter,
      hasHover: !!hoveredShiftId,
      hasOpenSelect: !!shiftWithOpenSelect,
      activeEmployeeFilter: selectedEmployeeFilter,
      activeStatusFilter: selectedStatusFilter,
      hoveredShift: hoveredShiftId,
      openSelectShift: shiftWithOpenSelect,
    };
  }, [selectedEmployeeFilter, selectedStatusFilter, hoveredShiftId, shiftWithOpenSelect]);

  // Check if any dimming is currently active
  const isDimmingActive = useCallback(() => {
    return !!(selectedEmployeeFilter || selectedStatusFilter || hoveredShiftId || shiftWithOpenSelect);
  }, [selectedEmployeeFilter, selectedStatusFilter, hoveredShiftId, shiftWithOpenSelect]);

  // Tooltip management functions
  const handleTooltipShow = useCallback((shiftId: string, preferredSide?: 'top' | 'bottom' | 'left' | 'right') => {
    setActiveTooltipShiftId(shiftId, preferredSide);
  }, [setActiveTooltipShiftId]);

  const handleTooltipHide = useCallback(() => {
    setActiveTooltipShiftId(null);
  }, [setActiveTooltipShiftId]);

  return {
    // State
    selectedEmployeeFilter,
    selectedStatusFilter,
    hoveredShiftId,
    shiftWithOpenSelect,
    activeTooltipShiftId,
    draggingShiftId,

    // Core functions
    getShiftDimmingClass,
    isShiftHighlighted,

    // Drag state management
    setDraggingShiftId,
    
    // Employee filter management
    setEmployeeFilter,
    showAllEmployees,
    showUnassignedShifts,
    showEmployeeShifts,
    clearEmployeeFilter,
    shouldDimForEmployeeFilter,

    // Status filter management
    setStatusFilter,
    showAllStatuses,
    showStatusShifts,
    clearStatusFilter,
    shouldDimForStatusFilter,

    // Hover management
    handleShiftHover,

    // Select management
    handleSelectOpen,
    handleSelectClose,

    // Tooltip coordination
    shouldShowTooltip,
    getTooltipSide,
    setActiveTooltipShiftId,
    handleTooltipShow,
    handleTooltipHide,

    // Utility functions
    getDimmingState,
    isDimmingActive,
    clearAllDimming,
  };
};

export default useDimming;